import { useEffect, useRef, useState } from 'react';

// In-page, «Granito & Ouro»-themed colour picker — a saturation/value area, a
// hue slider, a hex field and preset swatches. Deliberately avoids the native
// <input type="color"> OS chrome. Value is a #rrggbb hex string.

type HSV = { h: number; s: number; v: number };

function clamp(n: number, lo = 0, hi = 1) {
  return Math.min(hi, Math.max(lo, n));
}

function hexToRgb(hex: string): [number, number, number] {
  const c = hex.replace('#', '');
  const full =
    c.length === 3
      ? c
          .split('')
          .map((x) => x + x)
          .join('')
      : c;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((x) =>
        Math.round(clamp(x, 0, 255))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

function rgbToHsv(r: number, g: number, b: number): HSV {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

function hsvToRgb({ h, s, v }: HSV): [number, number, number] {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const PRESETS = ['#16293c', '#8a6a34', '#2c6b38', '#6a2580', '#8a1c1c', '#1d4e89', '#3a3a3a'];

export function ColorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(value);
  const [hsv, setHsv] = useState<HSV>(() => {
    const [r, g, b] = hexToRgb(value);
    return rgbToHsv(r, g, b);
  });
  const wrapRef = useRef<HTMLDivElement>(null);

  // Seed the editor state from the current value when opening the popover.
  const openPicker = () => {
    setHex(value);
    const [r, g, b] = hexToRgb(value);
    setHsv(rgbToHsv(r, g, b));
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const commit = (next: HSV) => {
    setHsv(next);
    const [r, g, b] = hsvToRgb(next);
    const h = rgbToHex(r, g, b);
    setHex(h);
    onChange(h);
  };

  const hueHex = rgbToHex(...hsvToRgb({ h: hsv.h, s: 1, v: 1 }));

  const pick = (kind: 'sv' | 'hue') => (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.buttons === 0 && e.type === 'pointermove') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = clamp((e.clientX - rect.left) / rect.width);
    if (kind === 'hue') {
      commit({ ...hsv, h: px * 360 });
    } else {
      const py = clamp((e.clientY - rect.top) / rect.height);
      commit({ ...hsv, s: px, v: 1 - py });
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPicker())}
        className="border-line bg-surface hover:border-primary flex items-center gap-2 rounded-md border px-2 py-1.5"
        aria-label="Escolher cor"
      >
        <span
          className="border-line h-6 w-6 rounded"
          style={{ backgroundColor: value }}
          aria-hidden="true"
        />
        <span className="text-on-surface font-mono text-xs">{value}</span>
      </button>

      {open && (
        <div className="border-line bg-surface absolute z-20 mt-2 w-56 rounded-lg border p-3 shadow-xl">
          <div
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              pick('sv')(e);
            }}
            onPointerMove={pick('sv')}
            className="relative h-32 w-full cursor-crosshair rounded-md"
            style={{
              backgroundColor: hueHex,
              backgroundImage:
                'linear-gradient(to right, #fff, transparent), linear-gradient(to top, #000, transparent)',
            }}
          >
            <span
              className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
            />
          </div>

          <div
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              pick('hue')(e);
            }}
            onPointerMove={pick('hue')}
            className="relative mt-3 h-3 w-full cursor-pointer rounded-full"
            style={{
              backgroundImage:
                'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
            }}
          >
            <span
              className="pointer-events-none absolute top-1/2 h-4 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
              style={{ left: `${(hsv.h / 360) * 100}%` }}
            />
          </div>

          <input
            className="border-line bg-background text-on-surface focus:border-primary mt-3 w-full rounded-md border px-2 py-1 font-mono text-xs outline-none"
            value={hex}
            onChange={(e) => {
              const v = e.target.value;
              setHex(v);
              if (HEX_RE.test(v)) {
                const [r, g, b] = hexToRgb(v);
                setHsv(rgbToHsv(r, g, b));
                onChange(v);
              }
            }}
          />

          <div className="mt-3 flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                aria-label={`Cor ${p}`}
                onClick={() => {
                  const [r, g, b] = hexToRgb(p);
                  commit(rgbToHsv(r, g, b));
                }}
                className="border-line h-5 w-5 rounded border"
                style={{ backgroundColor: p }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
